/* The words of the Profile history tab, one function per vocabulary.
 *
 * **A `switch` per union rather than a template key**, for two reasons that are each a defect this
 * codebase has shipped. `t(`profile.compare.status.${status}`)` is invisible to the copy test's
 * static-key sweep, so a renamed status would render its own key at a customer; and a `switch` over a
 * closed union with a declared return type is a compile error the day a sixth status arrives, where a
 * template key would render it untranslated with every gate green.
 */

import type { TFunction } from 'i18next';

import type {
  ProfileRecommendation,
  ProfileRunSummary,
} from '@/shared/api/generated/catalog';

import type {
  ComparedMetric,
  ComparisonStatus,
  Movement,
  RecommendationSeverity,
} from '../model/history';
import { instantLabel, recommendationValues } from '../model/history';

export function statusLabel(t: TFunction, status: ComparisonStatus): string {
  switch (status) {
    case 'degraded':
      return t('profile.compare.status.degraded');
    case 'improved':
      return t('profile.compare.status.improved');
    case 'changed':
      return t('profile.compare.status.changed');
    case 'unchanged':
      return t('profile.compare.status.unchanged');
    case 'not_comparable':
      return t('profile.compare.status.not_comparable');
  }
}

/** The status inside a sentence: "−4.0 pts · worse", "Only worse". */
export function statusWord(t: TFunction, status: ComparisonStatus): string {
  switch (status) {
    case 'degraded':
      return t('profile.compare.word.degraded');
    case 'improved':
      return t('profile.compare.word.improved');
    case 'changed':
      return t('profile.compare.word.changed');
    case 'unchanged':
      return t('profile.compare.word.unchanged');
    case 'not_comparable':
      return t('profile.compare.word.not_comparable');
  }
}

export function statusMeaning(t: TFunction, status: ComparisonStatus): string {
  switch (status) {
    case 'degraded':
      return t('profile.compare.statusMeaning.degraded');
    case 'improved':
      return t('profile.compare.statusMeaning.improved');
    case 'changed':
      return t('profile.compare.statusMeaning.changed');
    case 'unchanged':
      return t('profile.compare.statusMeaning.unchanged');
    case 'not_comparable':
      return t('profile.compare.statusMeaning.not_comparable');
  }
}

export function metricLabel(t: TFunction, metric: ComparedMetric): string {
  switch (metric) {
    case 'rows':
      return t('profile.compare.metric.rows');
    case 'baseline':
      return t('profile.compare.metric.baseline');
    case 'completeness':
      return t('profile.compare.metric.completeness');
    case 'distinct_count':
      return t('profile.compare.metric.distinct_count');
    case 'dominant_share':
      return t('profile.compare.metric.dominant_share');
  }
}

/** A dataset measure inside the headline: "the profile baseline fell". */
export function measureWord(t: TFunction, metric: ComparedMetric): string {
  return metric === 'rows'
    ? t('profile.compare.measure.rows')
    : t('profile.compare.measure.baseline');
}

export function severityLabel(t: TFunction, severity: RecommendationSeverity): string {
  switch (severity) {
    case 'high':
      return t('profile.recommendation.severity.high');
    case 'medium':
      return t('profile.recommendation.severity.medium');
    case 'low':
      return t('profile.recommendation.severity.low');
  }
}

export function recommendationText(
  t: TFunction,
  recommendation: ProfileRecommendation,
): string {
  const values = recommendationValues(recommendation);
  switch (recommendation.kind) {
    case 'became_all_null':
      return t('profile.recommendation.became_all_null', values);
    case 'completeness_dropped':
      return t('profile.recommendation.completeness_dropped', values);
    case 'became_constant':
      return t('profile.recommendation.became_constant', values);
    case 'dominance_increased':
      return t('profile.recommendation.dominance_increased', values);
    case 'rows_dropped':
      return t('profile.recommendation.rows_dropped', values);
    case 'baseline_dropped':
      return t('profile.recommendation.baseline_dropped', values);
    case 'column_dropped':
      return t('profile.recommendation.column_dropped', values);
  }
}

export function movementText(t: TFunction, move: Movement): string {
  switch (move.kind) {
    case 'none':
      return t('profile.compare.move.none');
    case 'notComparable':
      return t('profile.compare.move.notComparable');
    case 'points':
      return t('profile.compare.move.points', { value: move.value });
    case 'count':
      return move.value;
    case 'approxPercent':
      return t('profile.compare.move.approx', { value: move.value });
    case 'rows':
      return move.percent === null
        ? move.value
        : t('profile.compare.move.rows', { value: move.value, percent: move.percent });
    case 'baseline':
      return t('profile.compare.move.baseline', { value: move.value });
  }
}

/** One side of a measure, with "not measured" in words where the platform measured nothing. */
export function valueOrNotMeasured(t: TFunction, value: string | null): string {
  return value ?? t('profile.compare.notMeasured');
}

/** A run as a picker option names it: `15 Oct 2026, 09:02 · schema v3`, and `· grouped` for a run
 * stored before run markers existed. */
export function runOption(t: TFunction, run: ProfileRunSummary): string {
  return [
    instantLabel(run.finished_at),
    run.version_number === null
      ? null
      : t('profile.history.run.schema', { version: run.version_number }),
    run.tracked ? null : t('profile.history.run.grouped'),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/** What a run measured: `12 columns · 1,000 rows`. */
export function runFacts(t: TFunction, run: ProfileRunSummary): string {
  return [
    t('profile.history.run.columns', { count: run.columns_profiled }),
    t('profile.history.run.rows', { count: run.rows_scanned }),
  ].join(' · ');
}
